// Shared News Layout — used by both v1 and v2 with `theme` prop ("newsv1" or "newsv2")
import { useEffect, useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Search, Sun, Moon, ArrowRight, PenLine, CloudRain, CloudSun, Cloud, CloudDrizzle } from "lucide-react";
import { PUB, SECTIONS, TICKER } from "@/lib/news";

export default function NewsLayout({ theme = "newsv1", base = "/v1" }) {
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const loc = useLocation();
  useEffect(() => { setOpen(false); setShowSearch(false); window.scrollTo({top:0}); }, [loc.pathname]);
  const today = new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const isV1 = theme === "newsv1";

  return (
    <div className={`theme-${theme} ${dark?"dark":""} variant-root`} data-testid={`${theme}-layout`}>
      {/* WRITE FOR US ANNOUNCEMENT — very top, links to /contact */}
      <Link
        to={`${base}/contact`}
        data-testid="write-for-us-band"
        className="group block border-b relative overflow-hidden transition"
        style={{background:"var(--fg)", color:"var(--bg)", borderColor:"var(--line)"}}
      >
        {/* soft diagonal shine */}
        <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{background:"linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.10) 50%, transparent 70%)"}}/>
        <div className="relative flex items-center justify-center gap-4 px-4 py-3 md:py-3.5 text-center flex-wrap">
          <span className="hidden md:inline-flex items-center gap-2 font-cond text-[11px] tracking-[0.28em] px-3 py-1 border" style={{borderColor:"rgba(255,255,255,0.35)", background:"rgba(255,255,255,0.06)"}}>
            <PenLine size={13}/> OPEN CALL FOR WRITERS
          </span>
          <span className="font-italic text-[14px] md:text-[17px] leading-tight">
            Have a story, an op-ed, or a first-person piece?
          </span>
          <span className="inline-flex items-center gap-2 font-cond text-[11px] md:text-[12px] tracking-[0.24em] px-4 py-1.5 group-hover:translate-x-1 transition" style={{background:"var(--accent)", color:"var(--accent-fg)"}}>
            WRITE FOR US <ArrowRight size={13}/>
          </span>
        </div>
      </Link>

      {/* TOP UTILITY BAR */}
      <div className="border-b font-mono text-[10px] uppercase tracking-[0.14em] flex items-center justify-between px-4 lg:px-8 h-7" style={{borderColor:"var(--line-soft)", background:"var(--bg-elev)"}}>
        <span>{today}</span>
        <span className="hidden md:inline">{PUB.city} · 21°C · Sunny</span>
        <span className="flex items-center gap-3">
          <button onClick={()=>setDark(d=>!d)} data-testid={`${theme}-dark-toggle`} className="flex items-center gap-1 hover:opacity-80">{dark?<Sun size={11}/>:<Moon size={11}/>} {dark?"Light":"Dark"}</button>
          <Link to={`${base}/contact`} className="hover:opacity-80">Subscribe</Link>
          <Link to={`${base}/contact`} className="hover:opacity-80">Sign in</Link>
        </span>
      </div>

      {/* MASTHEAD */}
      <div className="border-b text-center py-4 lg:py-5 px-4" style={{borderColor:"var(--line)"}}>
        <Link to={base} data-testid={`${theme}-logo`} className="inline-block">
          <h1 className={`font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none ${isV1?"":""}`}>
            {isV1 ? <>World Media <span className="font-italic" style={{color:"var(--accent)"}}>Journal</span>.</> : <>WORLD MEDIA <span style={{color:"var(--accent)"}}>·</span> JOURNAL</>}
          </h1>
        </Link>
        <p className="mt-1.5 text-[10px] smcaps text-[var(--fg-mute)]">{PUB.tagline} · {PUB.founded}</p>
      </div>

      {/* PRIMARY NAV */}
      <header className="border-b sticky top-0 z-40 backdrop-blur" style={{borderColor:"var(--line)", background:"color-mix(in srgb, var(--bg) 92%, transparent)"}}>
        <div className="px-4 lg:px-8 h-11 grid grid-cols-12 items-center gap-3">
          <button onClick={()=>setOpen(true)} className="md:hidden p-1.5 col-span-2" data-testid={`${theme}-mobile-toggle`}><Menu size={18}/></button>
          <div className="hidden md:flex col-span-2 items-center gap-2" data-testid="au-flag">
            <img
              src="https://flagcdn.com/w80/au.png"
              alt="Australia"
              className="h-4 w-auto border shadow-sm"
              style={{borderColor:"var(--line-soft)"}}
              width="20"
              height="12"
            />
            <span className="font-cond text-[10px] tracking-[0.22em]" style={{color:"var(--fg-mute)"}}>EDITION · AU</span>
          </div>
          <nav className="hidden md:flex col-span-8 items-center justify-center gap-0">
            <NavLink to={base || "/"} end data-testid={`${theme}-nav-home`} className={({isActive}) => `px-4 py-2 text-[11px] font-cond border-r hover:bg-[var(--bg-elev)] ${isActive?"text-[var(--accent)]":""}`} style={{borderColor:"var(--line-soft)"}}>Home</NavLink>
            {SECTIONS.map((s) => (
              <NavLink key={s.slug} to={`${base}/section/${s.slug}`} data-testid={`${theme}-nav-${s.slug}`} className={({isActive}) => `px-4 py-2 text-[11px] font-cond border-r last:border-r-0 hover:bg-[var(--bg-elev)] ${isActive?"text-[var(--accent)]":""}`} style={{borderColor:"var(--line-soft)"}}>{s.short}</NavLink>
            ))}
          </nav>
          <div className="col-span-10 md:col-span-2 flex items-center gap-2 justify-end">
            <button onClick={()=>setShowSearch(s=>!s)} data-testid={`${theme}-search-btn`} className="p-1.5 hover:bg-[var(--bg-elev)] flex items-center gap-1"><Search size={14}/><span className="hidden lg:inline text-[11px]">Search</span></button>
            <Link to={`${base}/contact`} data-testid={`${theme}-cta-nav`} className={`hidden md:inline-flex px-3 py-1.5 text-[10px] font-cond ${isV1?"bg-[var(--fg)] text-[var(--bg)]":"accent-block"} hover:opacity-90`}>Subscribe</Link>
          </div>
        </div>
        {showSearch && (
          <div className="border-t px-4 lg:px-8 py-2.5" style={{borderColor:"var(--line-soft)"}} data-testid={`${theme}-search-bar`}>
            <input autoFocus placeholder="Search articles, authors, sections…" className="w-full bg-transparent border-b py-1.5 text-sm focus:outline-none focus:border-[var(--accent)]" style={{borderColor:"var(--line)"}}/>
          </div>
        )}
        {open && (
          <div className="md:hidden fixed inset-0 z-50" style={{background:"var(--bg)"}} data-testid={`${theme}-mobile-menu`}>
            <div className="border-b px-4 h-11 flex items-center justify-between" style={{borderColor:"var(--line)"}}>
              <span className="font-display text-2xl">World Media Journal</span>
              <button onClick={()=>setOpen(false)} className="p-1.5" data-testid={`${theme}-mobile-close`}><X size={20}/></button>
            </div>
            <div className="px-4 py-4 flex flex-col">
              <Link to={base} className="py-3 border-b font-display text-2xl" style={{borderColor:"var(--line-soft)"}}>Home</Link>
              {SECTIONS.map((s,i) => <Link key={s.slug} to={`${base}/section/${s.slug}`} className="py-3 border-b font-display text-2xl flex items-baseline gap-3" style={{borderColor:"var(--line-soft)"}}><span className="font-mono text-xs" style={{color:"var(--accent)"}}>§{String(i+1).padStart(2,"0")}</span>{s.name}</Link>)}
              <Link to={`${base}/contact`} className="py-3 border-b font-display text-2xl" style={{borderColor:"var(--line-soft)"}}>Subscribe</Link>
            </div>
          </div>
        )}
      </header>

      {/* LIVE CAPITALS WEATHER STRIP — no motion, all 8 capitals with icons */}
      <div className="border-b flex items-stretch text-xs" style={{borderColor:"var(--line)", background:"var(--fg)", color:"var(--bg)"}} data-testid="capitals-weather-strip">
        <span className="px-3 py-1.5 font-cond text-[10px] flex items-center gap-1.5 shrink-0" style={{background:"var(--accent2)", color:"var(--bg)"}}><span className="live-dot"/>LIVE · WEATHER</span>
        <div className="flex-1 flex items-center overflow-x-auto no-scrollbar">
          {[
            { k:"Sydney",    t:22, icon:Sun },
            { k:"Melbourne", t:16, icon:CloudRain },
            { k:"Brisbane",  t:27, icon:CloudSun },
            { k:"Perth",     t:30, icon:Sun },
            { k:"Adelaide",  t:24, icon:Cloud },
            { k:"Hobart",    t:12, icon:CloudDrizzle },
            { k:"Canberra",  t:14, icon:Cloud },
            { k:"Darwin",    t:33, icon:Sun },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.k} className="flex items-center gap-1.5 px-4 py-1.5 border-r whitespace-nowrap" style={{borderColor:"rgba(255,255,255,0.10)"}}>
                <Icon size={14} strokeWidth={1.6} className="text-white"/>
                <span className="font-cond text-[11px] tracking-wider">{c.k.toUpperCase()}</span>
                <span className="font-mono text-[11.5px] font-semibold tabular-nums">{c.t}°</span>
              </div>
            );
          })}
        </div>
      </div>

      <main><Outlet/></main>

      {/* GET IN TOUCH CTA — appears above the footer on every page */}
      <section className="border-t-[3px] border-b px-4 lg:px-8 py-14 lg:py-20" style={{borderColor:"var(--line)", background:"var(--fg)", color:"var(--bg)"}} data-testid="get-in-touch-band">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[11px] smcaps mb-3" style={{color:"var(--accent2)"}}>Find out more</p>
          <h2 className="text-headline text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
            Get in touch with <span className="font-italic" style={{color:"var(--accent2)"}}>World Media Journal.</span>
          </h2>
          <p className="font-italic text-base md:text-xl mt-5 max-w-2xl mx-auto opacity-80">
            Whether you have a question, a news tip, a business enquiry or would like more information, we&apos;re here to help.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs opacity-70">
            {["News tips","Editorial feedback","Corrections","Advertising & sponsorship","Business enquiries","Media requests","Partnership opportunities"].map(t => (
              <span key={t} className="font-cond">· {t}</span>
            ))}
          </div>
          <div className="mt-8 flex flex-col items-center gap-3">
            <Link
              to={`${base}/contact`}
              data-testid="contact-us-btn"
              className="inline-flex items-center gap-3 px-10 py-5 text-base font-cond tracking-[0.2em] hover:opacity-90 hover:translate-y-[-2px] transition"
              style={{background:"var(--accent)", color:"var(--accent-fg)"}}
            >
              CONTACT US <ArrowRight size={18}/>
            </Link>
            <Link to={`${base}/contact`} className="text-[13px] font-mono opacity-80 hover:opacity-100 underline underline-offset-4">Use our enquiry form →</Link>
            <p className="text-[11px] smcaps opacity-60">Editorial office · Sydney · Melbourne · Brisbane</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="mt-12 border-t-[3px]" style={{borderColor:"var(--line)"}}>
        <div className="px-4 lg:px-8 py-7 grid grid-cols-2 md:grid-cols-12 gap-5">
          <div className="md:col-span-5">
            <p className="font-display text-3xl">{isV1 ? <>World Media <span className="font-italic" style={{color:"var(--accent)"}}>Journal</span></> : <>WORLD MEDIA · JOURNAL</>}</p>
            <p className="mt-2 text-xs leading-relaxed max-w-xs text-[var(--fg-mute)]">{PUB.tagline}</p>
            <p className="mt-3 text-[10px] smcaps text-[var(--fg-mute)]">{PUB.founded} · {PUB.city}</p>
          </div>
          <div className="md:col-span-3">
            <p className="text-[10px] smcaps mb-1.5" style={{color:"var(--accent)"}}>Sections</p>
            <ul className="space-y-1 text-xs">{SECTIONS.map(s => <li key={s.slug}><Link to={`${base}/section/${s.slug}`} className="hover:text-[var(--accent)]">{s.name}</Link></li>)}</ul>
          </div>
          <div className="md:col-span-4">
            <p className="text-[10px] smcaps mb-1.5" style={{color:"var(--accent)"}}>Subscribe & Letters</p>
            <ul className="space-y-1 text-xs">
              <li><Link to={`${base}/contact`} className="hover:text-[var(--accent)]">Use our enquiry form</Link></li>
              <li><Link to={`${base}/contact`} className="hover:text-[var(--accent)]">Subscribe to the daily edition</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t px-4 lg:px-8 py-2.5 flex flex-wrap justify-between text-[10px] font-mono uppercase tracking-wider text-[var(--fg-mute)]" style={{borderColor:"var(--line-soft)"}}>
          <span>© {new Date().getFullYear()} {PUB.name} · all rights reserved</span>
          <span>Editorial standards · Privacy · Terms</span>
        </div>
      </footer>
    </div>
  );
}
